/**
 * The builder entrance stylesheet, generated FROM the preset records so a preset
 * cannot exist in the drawer and be missing from the CSS.
 *
 * Two properties the rules below are shaped around, both load-bearing:
 *
 * **Everything is scoped under `.pb-reveal-armed`.** The armed class is
 * ephemeral — the runtime adds it when it arms a node and removes it once the
 * transition ends. If the `transition` declaration lived on `[data-pb-reveal]`
 * instead, it would permanently replace whatever transition the template
 * authored on that element, and every hover effect on a card or button inside a
 * staggered container would stop working after the entrance finished.
 *
 * **Everything is gated on the `html[data-pb-motion-runtime]` handshake.** The
 * hidden state can only apply in a document where the runtime is present and
 * will reveal it, so no JS, a script error, or a stripped runtime all mean
 * "visible" rather than "invisible content that never appears".
 *
 * Zero `!important` — the golden export test caps the shipped budget at 20 and
 * an entrance stylesheet is not where that budget gets spent. NOT part of
 * critical CSS: it must not paint before the runtime can arm.
 */

import {
  ENTRANCE_PRESETS,
  ENTRANCE_ATTR,
  ENTRANCE_DELAY_ATTR,
  ENTRANCE_ARMED_CLASS,
  ENTRANCE_REVEALED_CLASS,
  ENTRANCE_DELAY_VALUES
} from './entrance-presets.js';

export const ENTRANCE_CSS_OPEN_MARKER = '/* pb-entrance-css */';
export const ENTRANCE_CSS_CLOSE_MARKER = '/* /pb-entrance-css */';

const RUNTIME_HANDSHAKE = 'html[data-pb-motion-runtime]';
const ARMED = `${RUNTIME_HANDSHAKE} [${ENTRANCE_ATTR}].${ENTRANCE_ARMED_CLASS}`;
const HIDDEN = `${ARMED}:not(.${ENTRANCE_REVEALED_CLASS})`;

function presetRule(preset) {
  if (!preset.hiddenTransform) return '';
  return `${RUNTIME_HANDSHAKE} [${ENTRANCE_ATTR}="${preset.id}"].${ENTRANCE_ARMED_CLASS}:not(.${ENTRANCE_REVEALED_CLASS}) {
  transform: ${preset.hiddenTransform};
}`;
}

function delayRule(ms) {
  return `${RUNTIME_HANDSHAKE} [${ENTRANCE_DELAY_ATTR}="${ms}"].${ENTRANCE_ARMED_CLASS} {
  transition-delay: ${ms}ms;
}`;
}

const ENTRANCE_CSS = [
  ENTRANCE_CSS_OPEN_MARKER,
  `${ARMED} {
  transition:
    opacity 0.6s ease,
    transform 0.6s ease;
}`,
  `${HIDDEN} {
  opacity: 0;
}`,
  ...ENTRANCE_PRESETS.map(presetRule).filter(Boolean),
  ...ENTRANCE_DELAY_VALUES.map(delayRule),
  `@media (prefers-reduced-motion: reduce) {
  ${ARMED} {
    transition: none;
  }

  ${HIDDEN} {
    opacity: 1;
    transform: none;
    transition: none;
  }
}`,
  ENTRANCE_CSS_CLOSE_MARKER
].join('\n\n');

/**
 * @returns {string} the entrance stylesheet. Callers gate emission on
 *   `stateUsesEntrance(state)`; an export with no preset set ships none of this.
 */
export function assembleEntranceCSS() {
  return ENTRANCE_CSS;
}
